import React, { useState } from 'react';
import { useAuth, useClerk, useSignUp } from '@clerk/clerk-react';
import { Link, useNavigate } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { useLanguage } from '../../contexts/useLanguage';
import { getClerkErrorMessage } from './clerkAuthErrors';
import { ClerkSignUpCardSkeleton } from './ClerkAuthCardSkeleton';
import { ClerkSecuredByFooter } from './ClerkSecuredByFooter';

type Phase = 'collect' | 'verify';

export interface CustomClerkSignUpFormProps {
  signInHref: string;
  afterSignUpRedirect?: string;
  /** When set, "Sign in" uses this instead of navigating to `signInHref`. */
  onPressSignIn?: () => void;
  /** Called after `setActive` succeeds, before navigation (e.g. close auth panel). */
  /** Return a path to navigate there; otherwise `afterSignUpRedirect` is used. */
  onAfterSignUpComplete?: () => string | void;
}

export function CustomClerkSignUpForm({
  signInHref,
  afterSignUpRedirect = '/client',
  onPressSignIn,
  onAfterSignUpComplete,
}: CustomClerkSignUpFormProps) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { isLoaded, signUp } = useSignUp();
  const { setActive } = useClerk();
  const { getToken } = useAuth();
  const saveSignUpAddress = useMutation(api.users.saveSignUpAddress);

  const [phase, setPhase] = useState<Phase>('collect');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [country, setCountry] = useState('Canada');
  const [province, setProvince] = useState('Québec');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const goAfterAuth = () => {
    const custom = onAfterSignUpComplete?.();
    const target =
      typeof custom === 'string' && custom.length > 0 ? custom : afterSignUpRedirect;
    navigate({ to: target });
  };

  const addressPayload = () => ({
    addressLine1: addressLine1.trim() || undefined,
    postalCode: postalCode.trim(),
    province: province.trim(),
    country: country.trim(),
  });

  const isAddressComplete = () => {
    const a = addressPayload();
    return [a.postalCode, a.country, a.province].every((s) => s.length > 0);
  };

  const handleCollect = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoaded || !signUp) return;
    if (!firstName.trim() || !lastName.trim()) {
      setError('Le prénom et le nom sont requis.');
      return;
    }
    if (!phone.trim()) {
      setError('Le numéro de téléphone est requis.');
      return;
    }
    if (!isAddressComplete()) {
      setError(t('clerkAuth.signUpAddressRequired'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await signUp.create({
        emailAddress: email,
        password,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      });

      await signUp.prepareEmailAddressVerification({
        strategy: 'email_code',
      });

      setPhase('verify');
    } catch (err) {
      setError(getClerkErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoaded || !signUp) return;
    setSubmitting(true);
    setError(null);
    try {
      const attempt = await signUp.attemptEmailAddressVerification({
        code,
      });

      if (attempt.status === 'complete' && attempt.createdSessionId) {
        await setActive({ session: attempt.createdSessionId });
        // Convex auth lags behind Clerk session; fetch Convex JWT before authenticated mutations.
        await getToken({ template: 'convex' });
        if (isAddressComplete()) {
          const emailTrimmed = email.trim();
          const addr = addressPayload();
          let saved = false;
          for (let i = 0; i < 4; i++) {
            try {
              await saveSignUpAddress({
                email: emailTrimmed,
                phone: phone.trim() || undefined,
                addressLine1: addr.addressLine1,
                postalCode: addr.postalCode,
                province: addr.province,
                country: addr.country,
              });
              saved = true;
              break;
            } catch (addrErr) {
              const msg = addrErr instanceof Error ? addrErr.message : String(addrErr);
              const retryable = /not authenticated|unauthenticated/i.test(msg) && i < 3;
              if (retryable) {
                await new Promise((r) => setTimeout(r, 120 * (i + 1)));
                await getToken({ template: 'convex' });
                continue;
              }
              console.warn('Convex saveSignUpAddress failed', addrErr);
              break;
            }
          }
          if (!saved) {
            // Clerk session is already active; do not trap the user on verify if Convex save fails.
            console.warn('saveSignUpAddress failed after sign-up; continuing into app');
            goAfterAuth();
            return;
          }
        }
        goAfterAuth();
        return;
      }

      setError(t('clerkAuth.errorSignUpIncomplete'));
    } catch (err) {
      setError(getClerkErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (!isLoaded || !signUp) return;
    setSubmitting(true);
    setError(null);
    try {
      await signUp.prepareEmailAddressVerification({
        strategy: 'email_code',
      });
    } catch (err) {
      setError(getClerkErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (!isLoaded) {
    return <ClerkSignUpCardSkeleton />;
  }

  return (
    <div className="w-full max-w-lg">
      <div className="rounded-2xl border border-zinc-200 bg-white p-8 shadow-sm">
        {phase === 'collect' ? (
          <>
            <h1 className="text-xl font-semibold tracking-tight text-zinc-900">
              {t('clerkAuth.signUpTitle')}
            </h1>
            <p className="mt-1 text-sm text-zinc-500">
              {t('clerkAuth.hasAccount')}{' '}
              {onPressSignIn ? (
                <button
                  type="button"
                  onClick={onPressSignIn}
                  className="font-medium text-emerald-700 hover:underline"
                >
                  {t('clerkAuth.signInLink')}
                </button>
              ) : (
                <Link to={signInHref} className="font-medium text-emerald-700 hover:underline">
                  {t('clerkAuth.signInLink')}
                </Link>
              )}
            </p>

            <form onSubmit={handleCollect} className="mt-8 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="clerk-signup-firstname" className="block text-sm font-medium text-zinc-700">
                    Prénom *
                  </label>
                  <input
                    id="clerk-signup-firstname"
                    name="firstName"
                    type="text"
                    autoComplete="given-name"
                    required
                    value={firstName}
                    onChange={(ev) => setFirstName(ev.target.value)}
                    placeholder="Marie"
                    className="mt-1.5 w-full rounded-lg border border-zinc-300 bg-white px-3 py-2.5 text-sm text-zinc-900 shadow-sm outline-none ring-emerald-500/0 transition-[box-shadow,border-color] focus:border-emerald-600 focus:ring-2 focus:ring-emerald-500/20"
                  />
                </div>
                <div>
                  <label htmlFor="clerk-signup-lastname" className="block text-sm font-medium text-zinc-700">
                    Nom *
                  </label>
                  <input
                    id="clerk-signup-lastname"
                    name="lastName"
                    type="text"
                    autoComplete="family-name"
                    required
                    value={lastName}
                    onChange={(ev) => setLastName(ev.target.value)}
                    placeholder="Tremblay"
                    className="mt-1.5 w-full rounded-lg border border-zinc-300 bg-white px-3 py-2.5 text-sm text-zinc-900 shadow-sm outline-none ring-emerald-500/0 transition-[box-shadow,border-color] focus:border-emerald-600 focus:ring-2 focus:ring-emerald-500/20"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="clerk-signup-email" className="block text-sm font-medium text-zinc-700">
                  {t('clerkAuth.email')}
                </label>
                <input
                  id="clerk-signup-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(ev) => setEmail(ev.target.value)}
                  className="mt-1.5 w-full rounded-lg border border-zinc-300 bg-white px-3 py-2.5 text-sm text-zinc-900 shadow-sm outline-none ring-emerald-500/0 transition-[box-shadow,border-color] focus:border-emerald-600 focus:ring-2 focus:ring-emerald-500/20"
                />
              </div>
              <div>
                <label htmlFor="clerk-signup-password" className="block text-sm font-medium text-zinc-700">
                  {t('clerkAuth.password')}
                </label>
                <input
                  id="clerk-signup-password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(ev) => setPassword(ev.target.value)}
                  className="mt-1.5 w-full rounded-lg border border-zinc-300 bg-white px-3 py-2.5 text-sm text-zinc-900 shadow-sm outline-none ring-emerald-500/0 transition-[box-shadow,border-color] focus:border-emerald-600 focus:ring-2 focus:ring-emerald-500/20"
                />
              </div>
              <div>
                <label htmlFor="clerk-signup-phone" className="block text-sm font-medium text-zinc-700">
                  Numéro de téléphone *
                </label>
                <input
                  id="clerk-signup-phone"
                  name="phone"
                  type="tel"
                  autoComplete="tel"
                  required
                  value={phone}
                  onChange={(ev) => setPhone(ev.target.value)}
                  placeholder="(514) 555-1234"
                  className="mt-1.5 w-full rounded-lg border border-zinc-300 bg-white px-3 py-2.5 text-sm text-zinc-900 shadow-sm outline-none ring-emerald-500/0 transition-[box-shadow,border-color] focus:border-emerald-600 focus:ring-2 focus:ring-emerald-500/20"
                />
              </div>

              <div className="space-y-3 border-t border-zinc-100 pt-6">
                <p className="text-sm font-medium text-zinc-800">{t('clerkAuth.signUpAddressSection')}</p>
                <div>
                  <label htmlFor="clerk-signup-street" className="block text-sm font-medium text-zinc-700">
                    Adresse ligne 1 <span className="text-zinc-400 font-normal">(optionnel)</span>
                  </label>
                  <input
                    id="clerk-signup-street"
                    name="addressLine1"
                    autoComplete="street-address"
                    value={addressLine1}
                    onChange={(ev) => setAddressLine1(ev.target.value)}
                    placeholder="123 Rue des Érables"
                    className="mt-1.5 w-full rounded-lg border border-zinc-300 bg-white px-3 py-2.5 text-sm text-zinc-900 shadow-sm outline-none ring-emerald-500/0 transition-[box-shadow,border-color] focus:border-emerald-600 focus:ring-2 focus:ring-emerald-500/20"
                  />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label htmlFor="clerk-signup-postal" className="block text-sm font-medium text-zinc-700">
                      {t('clerkAuth.signUpPostalCode')} *
                    </label>
                    <input
                      id="clerk-signup-postal"
                      name="postalCode"
                      required
                      autoComplete="postal-code"
                      value={postalCode}
                      onChange={(ev) => setPostalCode(ev.target.value)}
                      placeholder="H0H 0H0"
                      className="mt-1.5 w-full rounded-lg border border-zinc-300 bg-white px-3 py-2.5 text-sm text-zinc-900 shadow-sm outline-none ring-emerald-500/0 transition-[box-shadow,border-color] focus:border-emerald-600 focus:ring-2 focus:ring-emerald-500/20"
                    />
                  </div>
                  <div>
                    <label htmlFor="clerk-signup-province" className="block text-sm font-medium text-zinc-700">
                      {t('clerkAuth.signUpProvince')} *
                    </label>
                    <input
                      id="clerk-signup-province"
                      name="province"
                      required
                      autoComplete="address-level1"
                      value={province}
                      onChange={(ev) => setProvince(ev.target.value)}
                      placeholder="Québec"
                      className="mt-1.5 w-full rounded-lg border border-zinc-300 bg-white px-3 py-2.5 text-sm text-zinc-900 shadow-sm outline-none ring-emerald-500/0 transition-[box-shadow,border-color] focus:border-emerald-600 focus:ring-2 focus:ring-emerald-500/20"
                    />
                  </div>
                  <div>
                    <label htmlFor="clerk-signup-country" className="block text-sm font-medium text-zinc-700">
                      {t('clerkAuth.signUpCountry')} *
                    </label>
                    <input
                      id="clerk-signup-country"
                      name="country"
                      required
                      autoComplete="country-name"
                      value={country}
                      onChange={(ev) => setCountry(ev.target.value)}
                      placeholder="Canada"
                      className="mt-1.5 w-full rounded-lg border border-zinc-300 bg-white px-3 py-2.5 text-sm text-zinc-900 shadow-sm outline-none ring-emerald-500/0 transition-[box-shadow,border-color] focus:border-emerald-600 focus:ring-2 focus:ring-emerald-500/20"
                    />
                  </div>
                </div>
              </div>

              {error ? (
                <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
                  {error}
                </p>
              ) : null}

              <button
                type="submit"
                disabled={submitting}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-700 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {t('clerkAuth.signUpSubmit')}
              </button>
            </form>
          </>
        ) : (
          <>
            <h1 className="text-xl font-semibold tracking-tight text-zinc-900">
              {t('clerkAuth.verificationTitle')}
            </h1>
            <p className="mt-2 text-sm text-zinc-600">{t('clerkAuth.verificationHint')}</p>

            <form onSubmit={handleVerify} className="mt-8 space-y-4">
              <div>
                <label htmlFor="clerk-signup-code" className="block text-sm font-medium text-zinc-700">
                  {t('clerkAuth.codeLabel')}
                </label>
                <input
                  id="clerk-signup-code"
                  name="code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  value={code}
                  onChange={(ev) => setCode(ev.target.value)}
                  className="mt-1.5 w-full rounded-lg border border-zinc-300 bg-white px-3 py-2.5 text-center text-lg tracking-widest text-zinc-900 shadow-sm outline-none ring-emerald-500/0 transition-[box-shadow,border-color] focus:border-emerald-600 focus:ring-2 focus:ring-emerald-500/20"
                />
              </div>

              {error ? (
                <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
                  {error}
                </p>
              ) : null}

              <button
                type="submit"
                disabled={submitting}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-700 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {t('clerkAuth.verifySubmit')}
              </button>

              <div className="flex flex-col gap-2 sm:flex-row sm:justify-between">
                <button
                  type="button"
                  className="text-sm text-emerald-700 hover:underline disabled:opacity-50"
                  disabled={submitting}
                  onClick={handleResend}
                >
                  {t('clerkAuth.resendCode')}
                </button>
                <button
                  type="button"
                  className="text-sm text-zinc-600 hover:underline"
                  onClick={() => {
                    setPhase('collect');
                    setCode('');
                    setError(null);
                  }}
                >
                  {t('common.back')}
                </button>
              </div>
            </form>
          </>
        )}
      </div>

      <ClerkSecuredByFooter />
    </div>
  );
}
